<template>
  <BaseClearButton @click="photoModal?.$el?.present()" color="light" size="small">
    <i class="icon-image text-blue-500 mr-1"></i>
    <span class="text-blue-500 mr-1">{{ photos?.length || 0 }}</span>
    <span>Photo{{ photos?.length === 1 ? '' : 's' }}</span>
  </BaseClearButton>
  <IonModal ref="photoModal">
    <IonHeader class="ion-no-border px-[10px]">
      <IonToolbar color="white" class="border-b-2 border-gray-300">
        <IonTitle color="medium">{{ title || 'Comments' }}</IonTitle>
        <IonButtons slot="end">
          <IconButton icon="icon-x" @click="photoModal?.$el?.dismiss()"></IconButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>
    <IonContent>
      <IonRefresher :mode="refresherMode" v-if="props.onRefresh" slot="fixed" @ion-refresh="onRefresh">
        <IonRefresherContent :mode="refresherMode"></IonRefresherContent>
      </IonRefresher>
      <div v-if="!photos?.length" class="text-gray-500 italic p-4">No photos here yet...</div>
      <IonList lines="none" class="p-2">
        <PhotoRow v-for="p of photos" :key="p.name" :photo="p" @delete-clicked="onPhotoDelete(p.name)"></PhotoRow>
      </IonList>
    </IonContent>
    <IonFooter color="white" class="ion-no-border px-[10px]">
      <IonToolbar class="border-t-2 border-gray-300" color="white">
        <LightBlockButton :disabled="loading" @click="onTakePhotoClicked()">
          <div class="flex items-center" v-if="!loading"><i class="icon-camera mr-2 text-blue-500"></i> Take Photo</div>
          <div v-else class="flex items-center">
            <IonSpinner class="mr-1" name="lines-small" color="primary"></IonSpinner>
            Uploading
          </div>
        </LightBlockButton>
      </IonToolbar>
    </IonFooter>
  </IonModal>
</template>

<script setup lang="ts">
import { IDocument } from '@ncss/models/dist/v3';
import BaseClearButton from '../forms/BaseClearButton.vue';
import {
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonList,
  IonModal,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/vue';
import IconButton from '../forms/IconButton.vue';
import PhotoRow from '@/components/photos/PhotoRow.vue';
import LightBlockButton from '../forms/LightBlockButton.vue';
import { ref, useTemplateRef } from 'vue';
import { useCamera } from '@/composables/use-camera';
import { useToast } from '@/composables/use-toast';
import { useRefresherMode } from '@/composables/use-refresher-mode';

const props = defineProps<{
  title?: string;
  photos?: IDocument[];
  onRefresh?: (e?: CustomEvent) => Promise<void>;
  onRemovePhoto?: (name: string) => Promise<void>;
  onAddPhoto?: (base64: string) => Promise<void>;
}>();

const { mode: refresherMode } = useRefresherMode();
const photoModal = useTemplateRef('photoModal');
const loading = ref(false);
const { getPhoto } = useCamera();
const { successToast, errorToast } = useToast();

async function onPhotoDelete(name: string) {
  if (props.onRemovePhoto) {
    loading.value = true;
    try {
      await props.onRemovePhoto(name);
      await successToast('Photo removed!');
    } catch (e) {
      await errorToast('Failed to remove photo');
    }
    loading.value = false;
  }
}

async function onTakePhotoClicked() {
  if (props.onAddPhoto) {
    loading.value = true;
    const { error, base64, errorMsg } = await getPhoto();
    if (!error) {
      await props.onAddPhoto(base64);
      await successToast('Photo saved!');
    } else if (errorMsg) {
      await errorToast(errorMsg);
    }
    loading.value = false;
  }
}
</script>

<style scoped></style>
